import { useCallback, useEffect, useState, useMemo } from 'react';
import Checkbox from '../../checkbox';
import { useOptionById, useSelectContext } from '../context';
import { StyledMenuItem } from './SelectItem.styles';
import Tooltip from '../../tooltip';
import { useTexts } from '../hooks';
import useDropdownTexts from '../useTexts';
import { getOptionClass } from '../context/helpers';
import useKeyPress from '../hooks/useKeyPress';
import { useJourneyDataFiltersContext } from '../../../../features/resolutionPanel/dataFilters/dataFiltersContext';
import { MenuItemInnerContent } from './MenuItemInnerContent';

export const SelectItem = ({ id, elementType, isInFocus }) => {
  const option = useOptionById(id);
  const {
    selection,
    options,
    stylingVariant,
    isMultiple,
    keyString,
    captionString,
    classString,
    styleString,
    showHomeIcon,
    showArchiveIcon,
    isWideDropdown = false,
    toggleCheckOption,
    hasHierarchy,
    className,
    isCategory,
    isLeftCheckbox,
    fieldName,
  } = useSelectContext();
  const { isSupportDefault, isAllMessagesExpanded, handleSetDefault, handleToggleExpandAllMessages } =
    useJourneyDataFiltersContext();
  const { categoryNoValue, expandSwitch } = useTexts();
  const { closedStatusTooltipText } = useDropdownTexts();

  const [isChecked, setIsChecked] = useState(false); // do not remove state as for some reason the mui checkbox doesn't rerender if we don't use it
  const optionClass = hasHierarchy ? getOptionClass(id, options, keyString) : undefined;
  const isDisabled = optionClass && selection.valueClasses?.includes(optionClass);
  const isClosedStatus = option?.isClosedStatus || false;
  const isExpandButton = option?.valueCaption === expandSwitch;
  const enterPress = useKeyPress('Enter');

  const memoizedCheckedState = useMemo(() => {
    const selectedItems = hasHierarchy ? selection.values : selection;
    return Boolean(
      isMultiple &&
        (selectedItems?.map(String).includes(String(id)) ||
          (optionClass && selection.valueClasses?.includes(optionClass))),
    );
  }, [isMultiple, hasHierarchy, id, selection, optionClass]);

  useEffect(() => {
    setIsChecked(memoizedCheckedState);
  }, [memoizedCheckedState]);

  const toggleChecked = useCallback(() => {
    if (!isDisabled && !isClosedStatus && !isExpandButton) {
      toggleCheckOption(id);
    }
  }, [id, toggleCheckOption, isDisabled, isClosedStatus, isExpandButton]);

  useEffect(() => {
    if (enterPress && isInFocus) {
      toggleChecked();
    }
  }, [enterPress, toggleChecked, isInFocus]);

  const memoizedCheckbox = useMemo(
    () => (isMultiple ? <Checkbox checked={isChecked} disabled={isDisabled || isClosedStatus} /> : null),
    [isMultiple, isChecked, isDisabled, isClosedStatus],
  );

  const displayHomeIcon = showHomeIcon === option?.id;
  const displayArchiveIcon = showArchiveIcon === option?.id;
  const selectTitle = option[captionString] || (isCategory ? categoryNoValue : '');

  // Build data-cy attribute without nested template literals
  const baseValue = option ? option[keyString] : '';
  const typeClass = option?.valueClass !== null ? `-type-${option.valueClass ?? 'undefined'}` : '';
  const dataCyValue = option ? baseValue + typeClass : id;

  const menuItemInnerContent = (
    <MenuItemInnerContent
      isLeftCheckbox={isLeftCheckbox}
      isExpandButton={isExpandButton}
      displayHomeIcon={displayHomeIcon}
      displayArchiveIcon={displayArchiveIcon}
      memoizedCheckbox={memoizedCheckbox}
      stylingVariant={stylingVariant}
      option={option}
      classString={classString}
      styleString={styleString}
      selectTitle={selectTitle}
      isAllMessagesExpanded={isAllMessagesExpanded}
      handleToggleExpandAllMessages={handleToggleExpandAllMessages}
      isSupportDefault={isSupportDefault}
      handleSetDefault={handleSetDefault}
    />
  );

  const contentWithTooltip =
    isClosedStatus && fieldName === 'status' ? (
      <Tooltip title={closedStatusTooltipText} text={menuItemInnerContent} placement="top" />
    ) : (
      menuItemInnerContent
    );

  if (!option) {
    return null;
  }

  return (
    <StyledMenuItem
      className={`select-item ${className} ${option?.customCssClass || ''}`}
      key={option && option[keyString]}
      value={selectTitle}
      onClick={toggleChecked}
      checked={isChecked}
      disabled={isDisabled && !isClosedStatus}
      isClosedStatusDisabled={isClosedStatus}
      stylingVariant={stylingVariant}
      displayHomeIcon={displayHomeIcon}
      disableRipple
      component={elementType || undefined}
      data-testid={selectTitle}
      data-cy={dataCyValue}
      isMultiple={isMultiple}
      isWideDropdown={isWideDropdown}
      isInFocus={isInFocus}
      isExpandButton={isExpandButton}
      isLeftCheckbox={isLeftCheckbox}
      isSupportDefault={isSupportDefault}
    >
      {contentWithTooltip}
    </StyledMenuItem>
  );
};
